import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import JobBadge from './JobBadge';
import { jobs } from '../lib/jobs';
import TitanGuard from './TitanGuard';
import TopBar from './TopBar';
import Vault from './Vault';
import Studio from './Studio';
import ConfigPanel from './ConfigPanel';
import AdminPanel from './admin/AdminPanel';
import { usePlatform } from './BrandProvider';
import { Loader2 } from 'lucide-react';
import { loadSecure, saveSecure } from '../lib/crypto';
import { devToken, api } from '../lib/api';
import { db } from '../lib/db';
import { loadRuntime } from '../lib/runtime';
import { DEFAULT_SETTINGS, seedDemos, createGame } from '../lib/store';
import { migrateModel, sanitizeKey, providerOf } from '../lib/models';

const byDate = (a, b) => b.updatedAt - a.updatedAt;

function useAutoLock(minutes, active, onLock) {
  const last = useRef(Date.now());
  useEffect(() => {
    if (!active || !minutes) return;
    const bump = () => { last.current = Date.now(); };
    const evs = ['pointerdown', 'keydown', 'touchstart', 'wheel'];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const t = setInterval(() => { if (Date.now() - last.current > minutes * 60000) onLock(); }, 10000);
    return () => { clearInterval(t); evs.forEach((e) => window.removeEventListener(e, bump)); };
  }, [minutes, active, onLock]);
}

export default function DevArea() {
  const [phase, setPhase] = useState(devToken.get() ? 'checking' : 'locked');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [games, setGames] = useState([]);
  const gamesRef = useRef([]);
  const [activeId, setActiveId] = useState(null);
  const { features } = usePlatform();
  const [view, setViewState] = useState(() => { const v = sessionStorage.getItem('nexora.devview'); return v === 'config' || v === 'admin' ? v : 'vault'; });
  const setView = (v) => { sessionStorage.setItem('nexora.devview', v); setViewState(v); };
  const [command, setCommand] = useState(null);

  const unlock = async () => {
    await loadRuntime();
    const s = await loadSecure('settings');
    const merged = { ...DEFAULT_SETTINGS, ...(s || {}) };
    if (!merged.autoLockChosen) merged.autoLockMin = 0;
    merged.aiMode = 'byok'; // Emergent credits are disabled: only the owner's key is used
    const saved = merged.byokModel;
    merged.byokModel = migrateModel(saved);
    if (saved && saved !== merged.byokModel) toast(`Il modello ${saved} è stato ritirato dal provider: ora è selezionato ${merged.byokModel}`);
    merged.byokProvider = merged.byokProvider || providerOf(merged.byokModel);
    merged.apiKey = sanitizeKey(merged.apiKey);
    setSettings(merged);
    await seedDemos();
    const all = (await db.allGames()).sort(byDate);
    gamesRef.current = all;
    setGames(all);
    setPhase('open');
    jobs.resume();
    const want = sessionStorage.getItem('nexora.openGame') || jobs.get().meta?.gameId || jobs.get().failed?.meta?.gameId || (() => { try { return JSON.parse(localStorage.getItem('titan_active_generation_job'))?.gameId; } catch { return null; } })();
    sessionStorage.removeItem('nexora.openGame');
    if (want && all.some((g) => g.id === want)) { setActiveId(want); setViewState('studio'); }
  };

  const lock = useCallback(() => { devToken.set(null); setPhase('locked'); setSettings(DEFAULT_SETTINGS); }, []);
  useAutoLock(settings.autoLockMin, phase === 'open', lock);
  useEffect(() => {
    const h = () => { toast.error('Sessione sviluppatore scaduta: sblocca di nuovo'); lock(); };
    window.addEventListener('titan:unauth', h);
    return () => window.removeEventListener('titan:unauth', h);
  }, [lock]);

  useEffect(() => {
    if (phase !== 'checking') return;
    api('/dev/me', { auth: true, timeout: 8000 }).then(() => unlock()).catch((e) => { if (e.status) setPhase('locked'); else unlock(); });
  }, []); // eslint-disable-line

  const updateSettings = (patch) => {
    setSettings((cur) => { const n = { ...cur, ...patch }; saveSecure('settings', n); return n; });
  };

  const updateGame = useCallback((id, fn) => {
    const cur = gamesRef.current.find((g) => g.id === id);
    if (!cur) return null;
    const next = { ...fn(cur), updatedAt: Date.now() };
    gamesRef.current = gamesRef.current.map((g) => (g.id === id ? next : g));
    setGames(gamesRef.current);
    db.putGame(next);
    return next;
  }, []);

  useEffect(() => {
    const h = async (e) => {
      const g = await db.getGame(e.detail?.gameId);
      if (!g) return;
      gamesRef.current = gamesRef.current.map((x) => (x.id === g.id ? g : x));
      setGames(gamesRef.current);
    };
    window.addEventListener('titan:job-applied', h);
    return () => window.removeEventListener('titan:job-applied', h);
  }, []);

  const addGame = (g) => { gamesRef.current = [g, ...gamesRef.current]; setGames(gamesRef.current); };
  const removeGame = async (id) => {
    await db.delGame(id);
    gamesRef.current = gamesRef.current.filter((g) => g.id !== id);
    setGames(gamesRef.current);
    if (activeId === id) setActiveId(null);
  };
  const openGame = (id, cmd = null) => { setActiveId(id); setCommand(cmd); setView('studio'); };
  const cloneAs = async (title, code, genre) => {
    const g = await createGame({ title, code, genre, label: 'Clonato' });
    addGame(g);
    openGame(g.id);
  };

  if (phase === 'checking') return <div className="titan-root h-[100dvh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-violet-400" /></div>;
  if (phase !== 'open') return <TitanGuard onUnlock={unlock} />;

  const active = games.find((g) => g.id === activeId) || null;
  return (
    <div className="titan-root h-[100dvh] flex flex-col overflow-hidden" data-testid="titan-app">
      <TopBar view={view} setView={setView} active={active} onLock={lock} />
      <JobBadge hidden={view === 'studio'} onOpen={(id) => { if (id && gamesRef.current.some((g) => g.id === id)) openGame(id); }} />
      <main className="relative z-10 flex-1 min-h-0">
        {features.enableGameStudio && view === 'vault' && <Vault games={games} settings={settings} onOpen={openGame} onAdd={addGame} onDelete={removeGame} updateGame={updateGame} />}
        {features.enableGameStudio && view === 'studio' && (active
          ? <Studio key={active.id} game={active} settings={settings} updateSettings={updateSettings}
              updateGame={(fn) => updateGame(active.id, fn)} command={command} clearCommand={() => setCommand(null)} onClone={cloneAs} />
          : <EmptyStudio onVault={() => setView('vault')} />)}
        {view === 'config' && <ConfigPanel settings={settings} updateSettings={updateSettings} />}
        {(view === 'admin' || (!features.enableGameStudio && (view === 'vault' || view === 'studio'))) && <AdminPanel onExit={lock} onChangePin={() => setView('config')} />}
      </main>
    </div>
  );
}

function EmptyStudio({ onVault }) {
  return (
    <div className="h-full flex items-center justify-center p-8 text-center">
      <div className="glass rounded-2xl p-10 max-w-md rise">
        <div className="text-lg font-bold neon-text mb-2">Nessun gioco attivo</div>
        <p className="t-chat text-sm text-slate-400 mb-6">Apri un gioco dal Vault o forgiane uno nuovo con THE ARCHITECT.</p>
        <button data-testid="empty-studio-vault-btn" onClick={onVault} className="rounded-full px-5 py-2 text-sm font-bold bg-violet-600 hover:bg-violet-500 transition-colors">Vai al Vault</button>
      </div>
    </div>
  );
}
